import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

import DashboardMilestones from "./widgets/DashboardMilestones";
import DashboardProjects from "./widgets/DashboardProjects";
import DashboardComments from "./widgets/DashboardComments";
import RecentBids from "./widgets/RecentBids";
import PersonalShortcuts from "./widgets/PersonalShortcuts";
import OverdueInvoices from "./widgets/OverdueInvoices";
import UpcomingBills from "./widgets/UpcomingBills";
import DashboardClients from "./widgets/DashboardClients";

import useEntitlements from "@/auth/useEntitlements";

import {
    PERMISSION_GET_PROJECTS,
    PERMISSION_GET_BIDS,
    PERMISSION_GET_INVOICES,
    PERMISSION_GET_BILLS,
    PERMISSION_EDIT_CLIENTS,
} from "@/auth/constants";
import { FEATURE_BIDS, FEATURE_INVOICES } from "@/constants";

// ─── Widget Registry ────────────────────────────────────────────────────────────

const DEFAULT_HEIGHT = 1;
const DEFAULT_WIDTH = 1;

export const WIDGET_REGISTRY = {
    milestones: {
        label: "Upcoming Milestones",
        icon: <EventNoteIcon fontSize="small" />,
        defaultLayout: {
            w: DEFAULT_WIDTH,
            h: DEFAULT_HEIGHT,
            minW: 1,
            minH: DEFAULT_HEIGHT,
            maxH: DEFAULT_HEIGHT,
        },
        requiredPermission: PERMISSION_GET_PROJECTS,
    },
    projects: {
        label: "Projects",
        icon: <WorkOutlineOutlinedIcon fontSize="small" />,
        defaultLayout: {
            w: DEFAULT_WIDTH,
            h: DEFAULT_HEIGHT,
            minW: 1,
            minH: DEFAULT_HEIGHT,
            maxH: DEFAULT_HEIGHT,
        },
        requiredPermission: PERMISSION_GET_PROJECTS,
    },
    comments: {
        label: "Unresolved Comments",
        icon: <ChatBubbleOutlineIcon fontSize="small" />,
        defaultLayout: {
            w: DEFAULT_WIDTH,
            h: DEFAULT_HEIGHT,
            minW: 1,
            minH: DEFAULT_HEIGHT,
            maxH: DEFAULT_HEIGHT,
        },
    },

    recent_bids: {
        label: "Your Recent Bids",
        icon: <ArticleOutlinedIcon fontSize="small" />,
        defaultLayout: {
            w: DEFAULT_WIDTH,
            h: DEFAULT_HEIGHT,
            minW: 1,
            minH: DEFAULT_HEIGHT,
            maxH: DEFAULT_HEIGHT,
        },
        requiredPermission: PERMISSION_GET_BIDS,
        requiredFeature: FEATURE_BIDS,
    },
    personal_shortcuts: {
        label: "My Shortcuts",
        icon: <BookmarkBorderIcon fontSize="small" />,
        defaultLayout: {
            w: DEFAULT_WIDTH,
            h: DEFAULT_HEIGHT,
            minW: 1,
            minH: DEFAULT_HEIGHT,
            maxH: DEFAULT_HEIGHT,
        },
    },
    overdue_invoices: {
        label: "Overdue Invoices",
        icon: <WarningAmberIcon fontSize="small" />,
        defaultLayout: {
            w: 2,
            h: DEFAULT_HEIGHT,
            minW: 1,
            minH: DEFAULT_HEIGHT,
            maxH: DEFAULT_HEIGHT,
        },
        requiredPermission: PERMISSION_GET_INVOICES,
        requiredFeature: FEATURE_INVOICES,
    },
    upcoming_bills: {
        label: "Upcoming Bills",
        icon: <CalendarMonthOutlinedIcon fontSize="small" />,
        defaultLayout: {
            w: 2,
            h: DEFAULT_HEIGHT,
            minW: 1,
            minH: DEFAULT_HEIGHT,
            maxH: DEFAULT_HEIGHT,
        },
        requiredPermission: PERMISSION_GET_BILLS,
        requiredFeature: FEATURE_BIDS,
    },
    clients: {
        label: "My Clients",
        icon: <WorkOutlineOutlinedIcon fontSize="small" />,
        defaultLayout: {
            w: DEFAULT_WIDTH,
            h: DEFAULT_HEIGHT,
            minW: 1,
            minH: DEFAULT_HEIGHT,
            maxH: DEFAULT_HEIGHT,
        },
        requiredPermission: PERMISSION_EDIT_CLIENTS,
    },
};

// ─── Hook: check widget access using useEntitlements ────────────────────────────

export function useWidgetAccess() {
    const { hasPermission, hasRole, hasFeature } = useEntitlements();

    const isAvailable = (widgetId) => {
        const widgetDef = WIDGET_REGISTRY[widgetId];
        if (!widgetDef) return false;
        if (widgetDef.requiredRole && !hasRole(widgetDef.requiredRole))
            return false;
        if (
            widgetDef.requiredPermission &&
            !hasPermission(widgetDef.requiredPermission)
        )
            return false;
        if (widgetDef.requiredFeature && !hasFeature(widgetDef.requiredFeature))
            return false;
        return true;
    };

    return { isAvailable };
}

// ─── Helper: render the right component for a widget ID ─────────────────────────

export function renderWidget(widgetId, { personId, gridCols }) {
    switch (widgetId) {
        case "milestones":
            return <DashboardMilestones />;
        case "projects":
            return <DashboardProjects personId={`eq:${personId}`} />;
        case "comments":
            return <DashboardComments />;

        case "recent_bids":
            return <RecentBids />;
        case "personal_shortcuts":
            return <PersonalShortcuts gridCols={gridCols} />;
        case "overdue_invoices":
            return <OverdueInvoices personId={`eq:${personId}`} />;
        case "upcoming_bills":
            return <UpcomingBills />;
        case "clients":
            return <DashboardClients />;
        default:
            return null;
    }
}
